import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ArrowLeft, ArrowRight, Grid, Move, Maximize2 } from 'lucide-react'
import { Link } from 'react-router-dom'

// Fallback images
const images = [
  '/assets/DSC01966.jpg',
  '/assets/DSC01983.jpg',
  '/assets/DSC01986 (1).jpg',
  '/assets/DSC01986.jpg',
  '/assets/DSC02014.jpg',
  '/assets/DSC02098.jpg',
  '/assets/DSC02178.jpg',
  '/assets/DSC02181.jpg',
  '/assets/DSC02375.jpg',
  '/assets/DSC02382.jpg',
  '/assets/DSC02383.jpg',
  '/assets/DSC02392.jpg',
  '/assets/DSC02442.jpg',
  '/assets/DSC02452.jpg',
  '/assets/DSC02454(1).jpg',
  '/assets/DSC02454.jpg',
  '/assets/DSC02466.jpg',
  '/assets/DSC02479.jpg',
  '/assets/DSC02490.jpg',
  '/assets/DSC06271.jpg',
  '/assets/DSC06289.jpg',
  '/assets/DSC06312.jpg',
  '/assets/DSC_2511.jpg',
  '/assets/DSC_2531.jpg',
  '/assets/DSC_2591.jpg',
  '/assets/DSC_2670 (1).jpg',
  '/assets/DSC_2701.jpg',
  '/assets/FaceApp_1658542262955.jpg',
  '/assets/IMG_20220722_192304.jpg',
  '/assets/IMG_20220722_194747.jpg',
  '/assets/IMG_20240202_125115 (1).jpg',
  '/assets/IMG_20240202_132625 (1).jpg',
  '/assets/IMG_20240910_180713.jpg',
  '/assets/IMG_20250629_185154.jpg',
  '/assets/IMG_20250629_185311.jpg',
  '/assets/IMG_20250629_190313.jpg',
  '/assets/IMG_20250629_190349.jpg',
]

// Generate random positions for scatter mode
const scatteredPositions = images.map((_, i) => ({
  x: Math.random() * 2000 - 1000, // Spread across 2000px width
  y: Math.random() * 1500 - 750,  // Spread across 1500px height
  r: Math.random() * 30 - 15,     // Random rotation -15 to 15 deg
  scale: 0.8 + Math.random() * 0.4 // Random scale 0.8 to 1.2
}))

export default function Album() {
  const [viewMode, setViewMode] = useState<'scatter' | 'grid'>('scatter')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedId) return
      if (e.key === 'Escape') setSelectedId(null)
      if (e.key === 'ArrowRight') nextImage()
      if (e.key === 'ArrowLeft') prevImage()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedId, currentIndex])

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const openLightbox = (index: number) => {
    setCurrentIndex(index)
    setSelectedId(images[index])
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-black overflow-hidden selection:bg-rose-500 selection:text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-[#FDFBF7]/80 backdrop-blur-md border-b border-black/5">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-full border border-black/10 flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </div>
            <span className="font-mono text-xs uppercase tracking-widest hidden md:inline">Back</span>
          </Link>

          <div className="flex items-center gap-4">
            <h1 className="font-serif text-xl font-bold tracking-tight">
              SUDHA'S ACADEMY
            </h1>
            <span className="font-mono text-xs text-black/40 bg-black/5 px-2 py-1 rounded-full">
              {images.length}
            </span>
          </div>

          <div className="flex items-center gap-2 bg-black/5 p-1 rounded-full">
            <button
              onClick={() => setViewMode('scatter')}
              className={`p-2 rounded-full transition-all ${viewMode === 'scatter' ? 'bg-white shadow-sm' : 'hover:bg-black/5'}`}
              title="Scatter View"
            >
              <Move className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-full transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-black/5'}`}
              title="Grid View"
            >
              <Grid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative w-full h-screen pt-20">
        <AnimatePresence mode="wait">
          {viewMode === 'scatter' ? (
            <motion.div
              key="scatter"
              ref={containerRef}
              className="w-full h-full flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                drag
                dragConstraints={{ left: -1500, right: 1500, top: -1000, bottom: 1000 }}
                className="relative w-[3000px] h-[2000px] flex items-center justify-center"
              >
                {/* Hint */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                  <span className="font-serif text-9xl text-black/10 font-bold">DRAG TO EXPLORE</span>
                </div>

                {images.map((src, index) => (
                  <motion.div
                    key={src}
                    layoutId={`image-${src}`}
                    onClick={() => openLightbox(index)}
                    className="absolute w-64 md:w-80 bg-white p-3 shadow-xl cursor-pointer hover:z-50 transition-all duration-300 hover:scale-110"
                    style={{
                      x: scatteredPositions[index].x,
                      y: scatteredPositions[index].y,
                      rotate: scatteredPositions[index].r,
                    }}
                    whileHover={{ rotate: 0 }}
                  >
                    <div className="aspect-[3/4] overflow-hidden bg-gray-100">
                      <img src={src} alt="Archive" className="w-full h-full object-cover pointer-events-none" />
                    </div>
                    <div className="mt-2 flex justify-between items-center opacity-40">
                      <span className="font-mono text-[10px] uppercase">IMG_{index + 1}</span>
                      <span className="font-mono text-[10px] uppercase">SUDHA'S ACADEMY</span>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="grid"
              className="container mx-auto px-6 pb-20 pt-8 overflow-y-auto h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="columns-1 md:columns-3 lg:columns-4 gap-6 space-y-6">
                {images.map((src, index) => (
                  <motion.div
                    key={src}
                    layoutId={`image-${src}`}
                    onClick={() => openLightbox(index)}
                    className="break-inside-avoid relative group cursor-zoom-in overflow-hidden rounded-lg bg-gray-100"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                  >
                    <img
                      src={src}
                      alt="Portfolio"
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-xl flex items-center justify-center"
          >
            {/* Controls */}
            <button
              onClick={() => setSelectedId(null)}
              className="absolute top-8 right-8 w-12 h-12 rounded-full border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-colors z-50"
            >
              <X className="w-5 h-5" />
            </button>

            <button
              onClick={prevImage}
              className="absolute left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-colors z-50 hidden md:flex"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <button
              onClick={nextImage}
              className="absolute right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-colors z-50 hidden md:flex"
            >
              <ArrowRight className="w-5 h-5" />
            </button>

            {/* Image */}
            <motion.div
              layoutId={`image-${images[currentIndex]}`}
              className="relative w-full h-full max-w-6xl max-h-[85vh] p-4 md:p-12 flex items-center justify-center"
            >
              <img
                src={images[currentIndex]}
                alt="Full view"
                className="max-w-full max-h-full object-contain shadow-2xl"
              />

              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono text-xs tracking-widest text-black/50">
                {currentIndex + 1} / {images.length}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}